import React, { useState, useEffect } from 'react';
import { Calendar, Star, TrendingUp, Clock, ChevronDown, ChevronUp, Phone, Pencil } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import BookingDetails from '../components/BookingDetails';

const NAMES = {
  standard: 'Standardstädning', deep: 'Djupstädning', moving: 'Flyttstädning', office: 'Kontorsstädning',
  help: 'Flytthjälp', window: 'Fönsterputs', package: 'Månadspaket', minipaket: 'Minipaket'
};
const STATUS = {
  upcoming: ['KOMMANDE', '#dbeafe', '#1d4ed8'],
  completed: ['SLUTFÖRD', '#dcfce7', '#16a34a'],
  cancelled: ['AVBOKAD', '#fee2e2', '#dc2626']
};
const FIELDS = [['name', 'Namn'], ['phone', 'Telefon'], ['address', 'Adress'], ['city', 'Stad'], ['postal_code', 'Postnummer']];
const likeSafe = (s) => (s || '').replace(/[\\%_]/g, m => '\\' + m);
const cardStyle = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '24px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '15px' };

const CustomerDashboard = ({ user, onNavigate, onUserUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState({
    name: user.name || '', phone: user.phone || '', address: user.address || '',
    city: user.city || '', postal_code: user.postal_code || ''
  });

  useEffect(() => {
    supabase.from('bookings').select('*').ilike('email', likeSafe(user.email)).order('date', { ascending: false })
      .then(({ data }) => { setBookings(data || []); setLoading(false); });
  }, [user.email]);

  useEffect(() => {
    supabase.from('users').select('id, name, email, phone, address, city, postal_code, role').eq('id', user.id).limit(1)
      .then(({ data }) => {
        if (data && data[0]) {
          const u = data[0];
          setProfile({ name: u.name || '', phone: u.phone || '', address: u.address || '', city: u.city || '', postal_code: u.postal_code || '' });
          if (onUserUpdate) onUserUpdate({ ...user, ...u });
        }
      });
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    if (!profile.name.trim()) { alert('Namn kan inte vara tomt'); return; }
    setSaving(true);
    const { error } = await supabase.from('users').update(profile).eq('id', user.id);
    setSaving(false);
    if (error) { alert('Kunde inte spara: ' + error.message); return; }
    if (onUserUpdate) onUserUpdate({ ...user, ...profile });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const active = bookings.filter(b => b.status !== 'cancelled');
  const upcoming = bookings.filter(b => b.status === 'upcoming');
  const completed = bookings.filter(b => b.status === 'completed');
  const totalSpent = active.reduce((s, b) => s + (b.final_price || 0), 0);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-4xl mx-auto">

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1" style={{ color: '#1a1a2e' }}>Välkommen hem, {profile.name || user.name}! 👋</h1>
          <p style={{ color: '#888' }}>Här kan du se dina bokningar och hantera dina uppgifter.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: <Calendar className="w-6 h-6" />, label: 'Totalt Bokningar', value: active.length, color: '#1a1a2e' },
            { icon: <Clock className="w-6 h-6" />, label: 'Kommande', value: upcoming.length, color: '#3b82f6' },
            { icon: <Star className="w-6 h-6" />, label: 'Slutförda', value: completed.length, color: '#16a34a' },
            { icon: <TrendingUp className="w-6 h-6" />, label: 'Totalt Betalat', value: `${Math.round(totalSpent).toLocaleString()} kr`, color: '#C9A84C' }
          ].map((s, i) => (
            <div key={i} style={cardStyle}>
              <div className="mb-2" style={{ color: s.color }}>{s.icon}</div>
              <p className="text-sm mb-1" style={{ color: '#888' }}>{s.label}</p>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="p-6 rounded-2xl mb-8 flex flex-wrap items-center justify-between gap-4" style={{ backgroundColor: '#1a1a2e' }}>
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Boka en ny städning?</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>Snabbt och enkelt med 50% RUT-avdrag</p>
          </div>
          <button onClick={() => onNavigate('booking')} className="px-6 py-3 rounded-full font-bold" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
            Boka Nu →
          </button>
        </div>

        {/* Profile */}
        <div style={cardStyle} className="mb-8">
          <div className="flex justify-between items-center flex-wrap gap-3 mb-3">
            <h2 className="text-xl font-bold" style={{ color: '#1a1a2e' }}>Mina uppgifter</h2>
            {!editing && (
              <button onClick={() => setEditing(true)} className="px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2" style={{ backgroundColor: '#f8f7f4', border: '2px solid #e8e4d9', color: '#1a1a2e' }}>
                <Pencil className="w-4 h-4" /> Redigera
              </button>
            )}
          </div>
          {saved && <p className="text-sm font-semibold mb-3" style={{ color: '#16a34a' }}>✅ Dina uppgifter är sparade!</p>}
          {editing ? (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>{label}</label>
                    <input style={inp} value={profile[k] || ''} onChange={e => setProfile(p => ({ ...p, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <p className="text-xs mt-2" style={{ color: '#888' }}>Vill du byta email? Kontakta oss så hjälper vi dig.</p>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setEditing(false)} className="flex-1 py-3 rounded-full font-bold" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>Avbryt</button>
                <button onClick={saveProfile} disabled={saving} className="flex-1 py-3 rounded-full font-bold" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm space-y-1" style={{ color: '#555' }}>
              <p>👤 {profile.name || user.name}</p>
              <p>✉️ {user.email}</p>
              <p>📞 {profile.phone || '—'}</p>
              <p>📍 {profile.address || '—'}{profile.postal_code || profile.city ? `, ${profile.postal_code || ''} ${profile.city || ''}` : ''}</p>
            </div>
          )}
        </div>

        {/* Bookings */}
        <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Dina Bokningar</h2>

        {loading ? (
          <p style={{ color: '#888' }}>Laddar...</p>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 rounded-2xl" style={cardStyle}>
            <div className="text-5xl mb-4">🏠</div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#1a1a2e' }}>Inga bokningar ännu</h3>
            <p className="mb-6" style={{ color: '#888' }}>Boka din första städning idag!</p>
            <button onClick={() => onNavigate('booking')} className="px-8 py-3 rounded-full font-bold" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
              Boka Nu
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => {
              const st = STATUS[b.status] || STATUS.upcoming;
              const open = openId === b.id;
              return (
                <div key={b.id} style={cardStyle}>
                  <button onClick={() => setOpenId(open ? null : b.id)} className="w-full text-left">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="font-bold text-lg" style={{ color: '#1a1a2e' }}>{NAMES[b.service] || b.service}</h3>
                          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: st[1], color: st[2] }}>{st[0]}</span>
                        </div>
                        <p className="text-sm" style={{ color: '#888' }}>📅 {b.date} kl {b.time}</p>
                        <p className="text-sm" style={{ color: '#888' }}>📍 {b.address}, {b.city}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#C9A84C' }}>{Math.round(b.final_price || 0).toLocaleString()} kr</p>
                        <p className="text-xs" style={{ color: '#aaa' }}>{b.rut_discount > 0 ? 'du betalar (efter RUT)' : 'du betalar'}</p>
                        <p className="text-xs mt-2 font-semibold flex items-center justify-end gap-1" style={{ color: '#1a1a2e' }}>
                          {open ? 'Dölj detaljer' : 'Visa all info'} {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </p>
                      </div>
                    </div>
                  </button>

                  {open && (
                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #e8e4d9' }}>
                      <div className="mb-3 p-4 rounded-xl text-sm space-y-1" style={{ backgroundColor: '#f8f7f4' }}>
                        <div className="flex justify-between"><span style={{ color: '#888' }}>Totalt pris</span><span className="font-semibold">{Math.round(b.price || 0).toLocaleString()} kr</span></div>
                        {b.rut_discount > 0 && (
                          <div className="flex justify-between"><span style={{ color: '#16a34a' }}>RUT-avdrag (50%)</span><span className="font-semibold" style={{ color: '#16a34a' }}>-{Math.round(b.rut_discount).toLocaleString()} kr</span></div>
                        )}
                        <div className="flex justify-between"><span style={{ color: '#888' }}>Du betalar</span><span className="font-bold">{Math.round(b.final_price || 0).toLocaleString()} kr</span></div>
                      </div>
                      {b.details && <BookingDetails details={b.details} />}
                    </div>
                  )}

                  {b.status === 'upcoming' && (
                    <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#fef9ec', border: '1px solid #fde68a' }}>
                      <a href="tel:0739414366" className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
                        <Phone className="w-4 h-4" /> Ring för att Avboka
                      </a>
                      <p className="text-xs mt-2" style={{ color: '#92400e' }}>
                        Vill du avboka eller ändra din bokning behöver du ringa oss på 073-941 43 66.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;